import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  House,
  ChartLineUp,
  UsersThree,
  Calendar,
  ListChecks,
  ClipboardText,
  UserGear,
  SignOut,
  ChartBar,
  HourglassMedium,
  Target,
  Globe,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import NusacertLogo from "@/components/NusacertLogo";

const navItems = (role) => [
  { to: "/", label: "nav.dashboard", icon: House, testid: "nav-dashboard" },
  { to: "/pipeline", label: "nav.pipeline", icon: ChartLineUp, testid: "nav-pipeline" },
  { to: "/forecast", label: "nav.forecast", icon: Target, testid: "nav-forecast" },
  { to: "/contacts", label: "nav.contacts", icon: UsersThree, testid: "nav-contacts" },
  { to: "/leads-idle", label: "nav.leadsIdle", icon: HourglassMedium, testid: "nav-leads-idle" },
  { to: "/meetings", label: "nav.meetings", icon: Calendar, testid: "nav-meetings" },
  { to: "/tasks", label: "nav.tasks", icon: ListChecks, testid: "nav-tasks" },
  { to: "/activities", label: "nav.activities", icon: ClipboardText, testid: "nav-activities" },
  { to: "/reports", label: "nav.reports", icon: ChartBar, testid: "nav-reports" },
  ...(role === "admin" ? [{ to: "/team", label: "nav.users", icon: UserGear, testid: "nav-users" }] : []),
];

export default function Sidebar({ mobileOpen, onCloseMobile }) {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useI18n();
  const nav = useNavigate();

  const items = navItems(user?.role);

  const roleColorBadge = {
    admin: "bg-[#D9A321]/20 text-[#D9A321] border-[#D9A321]/30",
    manager: "bg-[#0F7180]/20 text-[#2BB8CA] border-[#0F7180]/40",
    sales: "bg-white/10 text-slate-300 border-white/20",
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-xs transition-opacity"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 w-[270px] max-w-[85vw] shrink-0 h-screen bg-[#173B4D] text-white flex flex-col transition-transform duration-300 ease-in-out border-r border-[#24566D]/40 shadow-xl lg:shadow-none ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Brand Header */}
        <div className="px-4 sm:px-5 py-4 border-b border-[#24566D]/50 flex items-center justify-between">
          <div className="bg-white rounded-xl px-3 py-2 shadow-xs w-full flex items-center justify-between">
            <img
              src="/nusacert-logo.png"
              alt="Nusacert"
              className="h-7 w-auto object-contain"
              data-testid="brand-name"
            />
            <span className="text-[9px] font-extrabold uppercase tracking-wider bg-[#173B4D] text-white px-2 py-0.5 rounded-md">
              CRM
            </span>
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 py-4 px-3 flex flex-col gap-1 overflow-y-auto">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={it.testid}
              onClick={() => onCloseMobile && onCloseMobile()}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2.5 rounded-md text-sm font-medium transition-all ${
                  isActive
                    ? "bg-[#0F7180] text-white shadow-xs"
                    : "text-slate-300 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <it.icon size={19} weight="duotone" className="shrink-0" />
              <span>{t(it.label)}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer: Language, User Profile & Logout */}
        <div className="p-3 border-t border-[#24566D]/50 space-y-2 bg-[#112B38]/50">
          {/* Language Switcher */}
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="w-full flex items-center justify-between px-3 py-2 rounded-md text-xs text-slate-300 hover:bg-white/10 transition-colors"
          >
            <span className="flex items-center gap-1.5">
              <Globe size={14} className="text-slate-400" />
              <span>Language</span>
            </span>
            <span className="font-mono text-xs font-semibold">
              <span className={lang === "id" ? "text-[#D9A321] font-bold" : "opacity-50"}>ID</span>
              <span className="mx-1 opacity-30">/</span>
              <span className={lang === "en" ? "text-[#D9A321] font-bold" : "opacity-50"}>EN</span>
            </span>
          </button>

          {/* User Profile Card */}
          <div className="flex items-center gap-2.5 px-3 py-2 bg-white/5 rounded-md border border-white/5">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.name}
                className="w-8 h-8 rounded-full object-cover border border-white/20"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[#0F7180] text-white flex items-center justify-center text-xs font-bold shrink-0">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div
                className="text-xs font-semibold truncate text-white"
                data-testid="sidebar-user-name"
              >
                {user?.name}
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                <span
                  className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.2 rounded border ${
                    roleColorBadge[user?.role] || roleColorBadge.sales
                  }`}
                >
                  {t(`role.${user?.role}`)}
                </span>
              </div>
            </div>
          </div>

          {/* Logout Button */}
          <button
            data-testid="logout-btn"
            onClick={async () => {
              await logout();
              nav("/login");
            }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
          >
            <SignOut size={16} />
            <span>{t("nav.logout")}</span>
          </button>
        </div>
      </aside>
    </>
  );
}
